import * as Yup from 'yup';

import { Alert, Button, Form, InputGroup } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { cleanMpan, formatMpanForDisplay } from '../../utils/formatMpanUtil';
import { contentfulPropTypes, onboardingPropTypes } from '../../propTypes';
import { error, light, primary, success, white } from '../../theme/colors';
import { flowSteps, recordFlowProgress } from '../../utils/recordFlowProgress';
import {
  validateAddress,
  validateMpan,
  validationCodes,
} from '../../api/SmartMeter';

import { Auth } from 'aws-amplify';
import CloseButton from '../../ui/Styled/CloseButton';
import { ConsentContext } from '../../context/ConsentContext';
import ErrorLabel from '../../ui/ErrorLabel';
import LoopSpinner from '../../ui/LoopSpinner';
import MpanWhatsThisModal from '../../ui/Modal/MpanWhatsThisModal';
import ReactMarkdown from 'react-markdown';
import TermsModal from '../../ui/Modal/TermsModal';
import checkboxEmpty from '../../assets/svg/check-box-empty.svg';
import checkboxError from '../../assets/svg/check-box-error.svg';
import checkboxFilled from '../../assets/svg/check-box-filled.svg';
import cx from 'classnames';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const StyledMarkdown = styled(ReactMarkdown)`
  text-align: left;
  cursor: pointer;

  p {
    em {
      text-decoration: underline;
      font-style: normal;
      color: ${primary};
    }
  }
`;

const StyledHR = styled.hr`
  height: 1px;
  border-top: solid 1px ${light};
`;

const StyledFormControl = styled(Form.Control)``;

const StyledCheckboxDiv = styled.div`
  margin-bottom: 10px;

  .form-check {
    padding-left: 0;
    background: url(${checkboxEmpty}) no-repeat top left;

    input {
      display: none;
    }
    .form-check-label {
      p {
        padding-left: 32px;
      }
    }
  }

  &.selected {
    .form-check {
      background-image: url(${checkboxFilled});
    }
  }
  &.error {
    .form-check {
      background-image: url(${checkboxError});
    }
  }

  .error-label {
    margin-left: 32px;
  }
`;

const StyledWhatsThisButton = styled(Button)`
  position: relative;
  top: -1px;
`;

const StyledFormGroup = styled(Form.Group)`
  &.user-supplied {
  }

  &.looked-up {
    .input-group {
      input.form-control {
        background: ${white};
        border-color: ${success};
        border-right: none;
      }
      .btn {
        border-left: 1px solid ${light};
        padding: 0 0 0 10px;
      }
    }
  }
`;

const StyledInputGroupText = styled(InputGroup.Text)`
  border-left: none;
  padding-top: 0;
  padding-bottom: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-color: ${success};
  background: ${white};
  padding-left: 0;
`;

const StyleErrorLabelButton = styled(Button)`
  margin: 0;
  padding: 0;
  color: ${error};
  font-size: 12px;
`;

const StyledErrorMarkdown = styled(ReactMarkdown)`
  p {
    margin-bottom: 0;
  }
`;

const MpanContainer = ({ contentful, loading, fetched }) => {
  const navigate = useNavigate();
  const {
    isManualAddress,
    lookedUpMpanHash,
    lookedUpMpanObfuscated,
    lookedUpMpanKnown,
    setMpan,
    consentFlow,
    setTermsAndConditions,
  } = useContext(ConsentContext);
  const [userSuppliedMpan, setUserSuppliedMpan] = useState(!lookedUpMpanKnown);
  const [showTerms, setShowTerms] = useState(false);
  const [showWhatsThis, setShowWhatsThis] = useState(false);
  const [fullTerms, setFullTerms] = useState(undefined);

  useEffect(() => {
    const asyncRecordFlowProgress = async () => {
      await recordFlowProgress(flowSteps.TERMS_AND_CONDITIONS);
      if (userSuppliedMpan) {
        await recordFlowProgress(flowSteps.MANUAL_MPAN_ENTRY);
      } else {
        await recordFlowProgress(
          flowSteps.MPAN_WITH_ADDRESS_AND_MPAN_PREFILLED_V2,
        );
      }
    };
    asyncRecordFlowProgress();
  }, [userSuppliedMpan]);

  const showFullTerms = e => {
    e.preventDefault();
    setShowTerms(true);
  };

  const onHideTerms = () => setShowTerms(false);

  const onHideWhatsThis = () => setShowWhatsThis(false);

  const onSubmit = async ({ mpan, ...values }) => {
    hideAlert();

    if (!!values.confirmTerms) {
      const termsAndConditions = {
        userSuppliedMpan,
        ...values,
        confirmTermsText: values.confirmTerms && contentful?.bodyExtra,
        confirmMpanText: values.confirmMpan && contentful?.bodyEnd,
        page: `${contentful?.title}\n\n${contentful?.subtitle}\n\n${contentful?.body}\n\n${contentful?.bodyEnd}`,
        modal: fullTerms
          ? `${fullTerms?.title}\n\n${fullTerms?.body}`
          : 'Full terms not viewed',
      };

      setTermsAndConditions(JSON.stringify(termsAndConditions));
    }

    // if user has supplied the mpan then continue with that, otherwise if confirmed the pre-filled obfuscated mpan then
    // continue with the associated hash version
    const finalisedMpan = userSuppliedMpan ? cleanMpan(mpan) : lookedUpMpanHash;

    let response;

    if (isManualAddress || userSuppliedMpan) {
      const user = await Auth.currentAuthenticatedUser();
      response = await validateAddress(
        finalisedMpan,
        user?.attributes['custom:postcode'].replace(' ', ''),
      );
    } else {
      response = await validateMpan(finalisedMpan);
    }

    if (response?.code === validationCodes.OK) {
      setMpan(finalisedMpan);
      window.scrollTo(0, 0);
      navigate(`/${consentFlow}/verify-address`);
    } else {
      if (response.code === validationCodes.MPAN_NOT_FOUND) {
        navigate(`/${consentFlow}/not-compatible`);
      } else {
        const message = !!response?.message ? '- ' + response.message : '';
        addAlert(
          'danger',
          `There was a problem submitting your MPAN number ${message}`,
        );
      }
    }
  };

  const regularMpanValidationRegex =
    /^[1-4][0-9]\s?[0-9]{4}\s?[0-9]{4}\s?[0-9]{3}$/;
  const obfuscatedMpanValidationRegex =
    /^[1-4][0-9]\s?[0-9]{4}\s?[0-9]{4}\s?---$/;

  // The regex varies depending if the mpan is obfuscated
  const mpanRegex = userSuppliedMpan
    ? regularMpanValidationRegex
    : obfuscatedMpanValidationRegex;

  // We only need confirmMpan to be required if not user supplied
  const confirmMpan = !userSuppliedMpan
    ? Yup.boolean()
        .required(contentful.utilityText)
        .oneOf([true], contentful.utilityText)
    : undefined;

  // TODO: Validate the booleans
  const validationSchema = Yup.object().shape({
    mpan: Yup.string()
      .matches(mpanRegex, 'MPAN must contain 13 digits, starting with a 1 or 2')
      .required('Please enter your 13 digit MPAN number'),
    confirmTerms: Yup.boolean()
      .required(contentful.utilityText)
      .oneOf([true], contentful.utilityText),
    confirmMpan,
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState(undefined);

  const addAlert = (variant, message) => {
    setShowAlert(true);
    setAlert({ variant, message });
  };

  const hideAlert = () => {
    setShowAlert(false);
    setAlert(undefined);
  };

  return (
    !loading &&
    fetched && (
      <Formik
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        initialValues={{
          confirmTerms: false,
          confirmMpan: false,
          mpan: formatMpanForDisplay(lookedUpMpanObfuscated),
        }}
        validateOnChange>
        {({ handleSubmit, isValid, isSubmitting, setFieldValue, ...rest }) => {
          const onEdit = () => {
            setUserSuppliedMpan(true);
            setFieldValue('mpan', '');
            onHideWhatsThis();
          };
          return (
            <Form onSubmit={handleSubmit}>
              {showTerms && (
                <TermsModal
                  showTerms={showTerms}
                  onHideTerms={onHideTerms}
                  setFullTerms={setFullTerms}
                />
              )}
              {showWhatsThis && (
                <MpanWhatsThisModal
                  showWhatsThis={showWhatsThis}
                  onHideWhatsThis={onHideWhatsThis}
                  onEdit={onEdit}
                />
              )}
              <p>
                Step <strong>2</strong> of <strong>3</strong>
              </p>
              <h1 className="pb-3">{contentful?.title}</h1>
              <div onClick={showFullTerms}>
                <StyledMarkdown>{contentful?.body}</StyledMarkdown>
              </div>
              <StyledHR />
              {showAlert && alert && (
                <div className="mb-4">
                  <Alert variant={alert.variant} onClose={() => hideAlert()}>
                    {alert.message}
                    <CloseButton onClick={() => hideAlert()} />
                  </Alert>
                </div>
              )}
              <Field name="confirmTerms">
                {({ field, meta }) => (
                  <StyledCheckboxDiv
                    className={`text-left ${cx({
                      selected: field.value,
                      error: !!meta.error,
                    })}`}>
                    <Form.Check
                      type="checkbox"
                      name="confirmTerms"
                      id="confirm-terms-check"
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      className={cx({ error: !!meta.error })}
                      label={
                        <StyledErrorMarkdown>
                          {contentful?.bodyExtra}
                        </StyledErrorMarkdown>
                      }
                    />
                    {meta.error && meta.touched && (
                      <ErrorLabel
                        pad={false}
                        showIcon={false}
                        label={meta.error}
                      />
                    )}
                  </StyledCheckboxDiv>
                )}
              </Field>
              {!userSuppliedMpan && (
                <Field name="confirmMpan">
                  {({ field, meta }) => (
                    <StyledCheckboxDiv
                      className={`text-left ${cx({
                        selected: field.value,
                        error: !!meta.error,
                      })}`}>
                      <Form.Check
                        type="checkbox"
                        name="confirmMpan"
                        id="confirm-mpan-check"
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className={cx({ error: !!meta.error })}
                        label={
                          <StyledErrorMarkdown>
                            {contentful?.bodyEnd}
                          </StyledErrorMarkdown>
                        }
                      />
                      {meta.error && meta.touched && (
                        <ErrorLabel
                          pad={false}
                          showIcon={false}
                          label={meta.error}
                        />
                      )}
                    </StyledCheckboxDiv>
                  )}
                </Field>
              )}
              <Field name="mpan">
                {({ field, meta }) => (
                  <StyledFormGroup
                    controlId="mpan"
                    className={cx({
                      'text-left': true,
                      'user-supplied': userSuppliedMpan,
                      'looked-up': !userSuppliedMpan,
                    })}>
                    <Form.Label>
                      MPAN{' '}
                      <StyledWhatsThisButton
                        onClick={() => setShowWhatsThis(true)}
                        variant="link"
                        className="m-0 p-0">
                        What's this?
                      </StyledWhatsThisButton>
                    </Form.Label>
                    <InputGroup className="mb-3">
                      <StyledFormControl
                        value={formatMpanForDisplay(field.value)}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={!userSuppliedMpan || isSubmitting}
                        isValid={!meta.error && meta.touched}
                        placeholder="00 0000 0000 000"
                        className={cx({
                          error: !!meta.error,
                          lookedup:
                            lookedUpMpanObfuscated === cleanMpan(field.value),
                        })}
                        maxLength="16"
                      />
                      {!userSuppliedMpan && (
                        <StyledInputGroupText>
                          <Button
                            onClick={onEdit}
                            variant="link"
                            className="m-0">
                            Edit
                          </Button>
                        </StyledInputGroupText>
                      )}
                    </InputGroup>
                    {userSuppliedMpan && meta.error && meta.touched && (
                      <ErrorLabel
                        label={
                          lookedUpMpanKnown ? (
                            <>
                              This MPAN doesn't look right, please try again or{' '}
                              <StyleErrorLabelButton
                                variant="link"
                                onClick={() => {
                                  setUserSuppliedMpan(false);
                                  setFieldValue('mpan', lookedUpMpanObfuscated);
                                }}>
                                use the previous MPAN
                              </StyleErrorLabelButton>{' '}
                              which Loop pre-filled
                            </>
                          ) : (
                            meta.error
                          )
                        }
                      />
                    )}
                  </StyledFormGroup>
                )}
              </Field>

              <Form.Group>
                {!isSubmitting && userSuppliedMpan && (
                  <Button
                    variant="primary"
                    className="w-100"
                    type="submit"
                    disabled={!isValid || isSubmitting}>
                    Continue
                  </Button>
                )}
                {!isSubmitting && !userSuppliedMpan && (
                  <>
                    {contentful?.primaryButtonText && (
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                        className="mt-2 mb-3 w-100">
                        {contentful.primaryButtonText}
                      </Button>
                    )}
                  </>
                )}

                {isSubmitting && <LoopSpinner />}
              </Form.Group>
            </Form>
          );
        }}
      </Formik>
    )
  );
};

MpanContainer.propTypes = {
  ...contentfulPropTypes,
  ...onboardingPropTypes,
};

export default withContentfulTag(
  MpanContainer,
  'web_app_onboarding_mpan_v3_page',
);
